import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Trash2 } from 'lucide-react';

import { PaneHeader } from '@/components/settings/pane-header';
import { SaveBar } from '@/components/settings/queues/save-bar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  normalizeTemplateDraft,
  renderTemplate,
  TEMPLATE_LIMITS,
  unresolvedTokens,
} from '@/lib/messages/message-templates';

// Group order for the variable palette. Mirrors the order the Go catalog declares its
// groups in; an unknown group (added server-side, not yet listed here) falls to the end
// rather than disappearing.
const GROUP_ORDER = ['Patient', 'Next appointment', 'Previous visit', 'Staff', 'Channel', 'Date helpers'];

function groupRank(group) {
  const index = GROUP_ORDER.indexOf(group);
  return index === -1 ? GROUP_ORDER.length : index;
}

/**
 * Detail pane for one message template: draft → dirty → explicit Save, the same
 * contract the queue editor uses.
 *
 * The variable palette and the live preview are the point of this screen. Staff
 * author a body with {{tokens}} they cannot be expected to remember, so the chips
 * insert them at the caret and the preview renders the result against the catalog's
 * example values — including the "N unresolved" warning, so a typo is visible here
 * rather than discovered as a missing line in a patient's chat.
 */
export function TemplateEditorPane({
  template,
  variables,
  sampleVars,
  canEditShared,
  onSaved,
  onDelete,
  onBack,
  saveTemplate,
}) {
  const [draft, setDraft] = useState(() => normalizeTemplateDraft(template));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const savedRef = useRef(normalizeTemplateDraft(template));
  const bodyRef = useRef(null);
  // Caret to restore after a chip insert — React drops the caret to the end of a
  // controlled textarea otherwise. Same mechanism as the composer's emoji insert.
  const pendingCaretRef = useRef(null);

  // Re-seed when the selected template changes (the list swaps rows in place).
  useEffect(() => {
    const next = normalizeTemplateDraft(template);
    savedRef.current = next;
    setDraft(next);
    setDirty(false);
  }, [template]);

  useEffect(() => {
    if (pendingCaretRef.current == null) return;
    const caret = pendingCaretRef.current;
    pendingCaretRef.current = null;
    bodyRef.current?.focus();
    bodyRef.current?.setSelectionRange(caret, caret);
  }, [draft.body]);

  const editable = draft.shared ? canEditShared : true;

  const editDraft = (patch) => {
    setDraft((current) => ({ ...current, ...patch }));
    setDirty(true);
  };

  const insertToken = (key) => {
    const textarea = bodyRef.current;
    const token = `{{${key}}}`;
    const body = String(draft.body || '');
    const start = textarea ? textarea.selectionStart : body.length;
    const end = textarea ? textarea.selectionEnd : start;
    pendingCaretRef.current = start + token.length;
    editDraft({ body: body.slice(0, start) + token + body.slice(end) });
  };

  const preview = useMemo(() => renderTemplate(draft.body, sampleVars), [draft.body, sampleVars]);
  const unresolved = useMemo(() => unresolvedTokens(draft.body, sampleVars), [draft.body, sampleVars]);

  const groupedVariables = useMemo(() => {
    const groups = new Map();
    for (const variable of variables ?? []) {
      const list = groups.get(variable.group) ?? [];
      list.push(variable);
      groups.set(variable.group, list);
    }
    return [...groups.entries()].sort((a, b) => groupRank(a[0]) - groupRank(b[0]));
  }, [variables]);

  const handleSave = async () => {
    if (!editable || saving) return;
    const normalized = normalizeTemplateDraft(draft);
    if (!normalized.title) {
      toast.error('Give the template a name.');
      return;
    }
    if (!normalized.body) {
      toast.error('The template body cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const updated = await saveTemplate(normalized);
      const next = normalizeTemplateDraft(updated);
      savedRef.current = next;
      setDraft(next);
      setDirty(false);
      onSaved?.(updated);
      toast.success(next.shared ? 'Template saved for everyone.' : 'Template saved.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the template');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraft(savedRef.current);
    setDirty(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        crumb={[{ label: 'Message templates', onClick: onBack }, { label: draft.title || 'Untitled template' }]}
        title={draft.title || 'Untitled template'}
        description={
          draft.shared
            ? 'Shared with your whole team. Everyone sees it in the composer.'
            : 'Private to you. Nobody else can see or use it.'
        }
        action={
          template?.id ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => onDelete?.(template)} disabled={!editable}>
              <Trash2 className="mr-1.5 size-4" aria-hidden />
              Delete
            </Button>
          ) : null
        }
      />

      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <div className="mx-auto max-w-3xl space-y-6">
          <section className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-foreground" htmlFor="template-title">
                Name
              </label>
              <Input
                id="template-title"
                value={draft.title}
                maxLength={TEMPLATE_LIMITS.title}
                onChange={(event) => editDraft({ title: event.target.value })}
                disabled={!editable}
                placeholder="Appointment reminder"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-foreground" htmlFor="template-category">
                Category
              </label>
              <Input
                id="template-category"
                value={draft.category}
                maxLength={TEMPLATE_LIMITS.category}
                onChange={(event) => editDraft({ category: event.target.value })}
                disabled={!editable}
                placeholder="Reminders"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-foreground" htmlFor="template-shortcut">
                Shortcut
              </label>
              <Input
                id="template-shortcut"
                value={draft.shortcut}
                maxLength={TEMPLATE_LIMITS.shortcut}
                onChange={(event) => editDraft({ shortcut: event.target.value })}
                disabled={!editable}
                placeholder="reminder"
              />
              <p className="text-[11px] text-muted-foreground">
                Type <code className="rounded bg-muted px-1">/{draft.shortcut || 'shortcut'}</code> in an empty
                message box to find this template fast. Letters, numbers, <code>-</code> and <code>_</code> only.
              </p>
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-semibold text-foreground">Visible to</span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={draft.shared ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => editDraft({ shared: true })}
                  disabled={!canEditShared}
                >
                  Whole clinic
                </Button>
                <Button
                  type="button"
                  variant={draft.shared ? 'outline' : 'default'}
                  size="sm"
                  onClick={() => editDraft({ shared: false })}
                  disabled={draft.shared && !canEditShared}
                >
                  Only me
                </Button>
              </div>
              {!canEditShared ? (
                <p className="text-[11px] text-muted-foreground">
                  You don’t have permission to edit shared settings, so you can only create personal templates.
                </p>
              ) : null}
            </div>
          </section>

          <section className="space-y-2">
            <label className="text-sm font-semibold text-foreground" htmlFor="template-body">
              Message
            </label>
            <Textarea
              id="template-body"
              ref={bodyRef}
              value={draft.body}
              maxLength={TEMPLATE_LIMITS.body}
              onChange={(event) => editDraft({ body: event.target.value })}
              disabled={!editable}
              rows={12}
              className="font-mono text-sm"
              placeholder={'Dear {{patientFullName}},\n\n*Date:* {{date}}\n*Time:* {{time}}'}
            />
            <p className="text-[11px] text-muted-foreground">
              WhatsApp formatting works: <code>*bold*</code>, <code>_italic_</code>. A label line whose value is
              empty (e.g. a missing address) is dropped automatically when the message is inserted.
            </p>
          </section>

          <section className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="text-xs font-semibold text-foreground">Variables</div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Click one to insert it where your cursor is.
            </p>
            <div className="mt-3 space-y-2.5">
              {groupedVariables.map(([group, groupVariables]) => (
                <div key={group}>
                  <div className="text-[10.5px] font-extrabold tracking-wide text-muted-foreground">
                    {group.toUpperCase()}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {groupVariables.map((variable) => (
                      <button
                        key={variable.key}
                        type="button"
                        onClick={() => insertToken(variable.key)}
                        disabled={!editable}
                        title={`${variable.label} — ${variable.description}\nExample: ${variable.example}`}
                        className="rounded bg-background px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)] transition hover:text-foreground disabled:opacity-50"
                      >{`{{${variable.key}}}`}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <div className="text-sm font-semibold text-foreground">Preview</div>
            <p className="text-[11px] text-muted-foreground">
              Rendered with example values. The real message uses the patient this chat is with.
            </p>
            {unresolved.length ? (
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-[12px] text-amber-700 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span>
                  {unresolved.length === 1 ? '1 variable is not recognised' : `${unresolved.length} variables are not recognised`}
                  {': '}
                  <span className="font-mono">{unresolved.map((token) => `{{${token}}}`).join(' ')}</span>. They will be
                  removed from the message — check the spelling against the list above.
                </span>
              </div>
            ) : null}
            {/* Capped + scrollable: a long template would otherwise push the save bar
                off the pane. The bound sits on the VIEWPORT — a max-h on the
                auto-height Root never constrains it (see ui/scroll-area.jsx). */}
            <ScrollArea
              className="rounded-lg border border-border bg-background"
              viewportClassName="max-h-72 p-3"
            >
              <pre className="whitespace-pre-wrap text-[13px] text-foreground">
                {preview || 'Nothing to preview yet.'}
              </pre>
            </ScrollArea>
          </section>
        </div>
      </ScrollArea>

      <SaveBar
        dirty={dirty}
        saving={saving}
        canSave={editable}
        onSave={handleSave}
        onDiscard={handleDiscard}
        saveLabel={draft.shared ? 'Save for everyone' : 'Save'}
      />
    </div>
  );
}
